import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  alpha,
  Slide,
  useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder"; // Ícone relacionado a cuidado/carinho
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

interface HideOnScrollProps {
  children: React.ReactElement;
}

function HideOnScroll(props: HideOnScrollProps) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export const Navbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const navItems = ["Cuidado", "Benefícios", "Nossos Planos", "Contato"];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const logo = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <FavoriteBorderIcon
        sx={{ color: theme.palette.secondary.main, fontSize: 28 }}
      />
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: theme.palette.text.primary }}
      >
        Plano Carinho
      </Typography>
    </Box>
  );

  return (
    <HideOnScroll>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: "space-between",
              height: 68,
            }}
          >
            {logo}

            {!isMobile && (
              <Box sx={{ mx: 2 }}>
                {navItems.map((item) => (
                  <Button
                    key={item}
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      mx: 1.2,
                      fontSize: "0.9rem",
                      borderRadius: 20,
                      "&:hover": {
                        color: theme.palette.secondary.dark,
                        backgroundColor: alpha(
                          theme.palette.secondary.light,
                          0.2
                        ),
                      },
                    }}
                    href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                href="#formulario"
                size="medium"
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                Solicitar Proposta
              </Button>

              {isMobile && (
                <IconButton
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: theme.palette.secondary.main,
                    backgroundColor: alpha(theme.palette.secondary.light, 0.2),
                    "&:hover": {
                      backgroundColor: alpha(
                        theme.palette.secondary.light,
                        0.3
                      ),
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>

        {/* Menu Mobile */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          PaperProps={{
            sx: {
              width: "80%",
              maxWidth: "320px",
              p: 2,
              borderTopRightRadius: theme.shape.borderRadius,
              borderBottomRightRadius: theme.shape.borderRadius,
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            {logo}
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ color: theme.palette.text.secondary }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <List sx={{ mb: 3 }}>
            {navItems.map((item) => (
              <ListItem disablePadding key={item} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={handleDrawerToggle}
                  component="a"
                  href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
                  sx={{
                    borderRadius: 1.5,
                    py: 1.5,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.light, 0.2),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: 36, color: theme.palette.primary.main }}
                  >
                    <LocalOfferIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            href="#formulario"
            sx={{ py: 1.5 }}
          >
            Solicitar Proposta
          </Button>
        </Drawer>
      </AppBar>
    </HideOnScroll>
  );
};
